import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dialog, DialogContent, DialogActions, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import i18next from 'i18next';
import { efaContentID, efaContentParentID } from '@/helpers/constants';
import { setHeaderLanguage } from '@/redux/Header/header.action';
import affectedVehicleServices from '@/services/affectedVehicleService';
import { Baumuster } from '@/types';
import { ThemeOptions } from '@/utils/componentStyles';
import styles from '../DialogNewCR/DialogNewCR.module.scss';
import { checkConnection } from '../DialogNewCR/efaWidget';
import ConfirmDialog from '../UI/Dialog/ConfirmDialog';
import Dropdown from '../UI/Dropdown/Dropdown';
import Spinner from '../UI/Spinner/Spinner';
import { prepareServiceWorker } from '../../serviceWorker';

interface DialogEVAWidgetInterface {
  close: () => void;
  setEvaFields?: (payload: {
    name?: string;
    value?: any;
    values?: Record<string, any>;
  }) => void;
}

const TYPE_VERSIONS = {
  0: { EN: '0 - Limousine', DE: '0 - Limousine' },
  1: { EN: '1 - Limousine Long', DE: '1 - Limousine lang' },
  2: { EN: '2 - Station Wagon', DE: '2 - Kombi' },
  3: { EN: '3 - Coupe / Coupe-Limousine', DE: '3 - Coupe / Coupe-Limousine' },
  4: { EN: '4 - Convertible / Roadster', DE: '4 - Cabrio / Roadster' },
  6: { EN: '6 - Chassis Long', DE: '6 - Chassis Long' },
  7: { EN: '7 - Limousine / SUV Compact', DE: '7 - Limousine / SUV Kompakt' },
  8: { EN: '8 - Limousine extra-long', DE: '8 - Limousine extra-lang' },
  9: { EN: '9 - Roadster', DE: '9 - Roadster' },
};

const STEERING = {
  1: { EN: '1 - Left (or both)', DE: '1 - Links (oder beide)' },
  2: { EN: '2 - Right (or both)', DE: '2 - Rechts (oder beide)' },
  5: { EN: '5 - Left CKD/SKD', DE: '5 - Links CKD/SKD' },
  6: { EN: '6 - Right CKD/SKD', DE: '6 - Rechts CKD/SKD' },
};

const useStyles = makeStyles(({ cartTheme }: ThemeOptions) => ({
  titleRoot: {
    borderBottom: `1px solid ${cartTheme.commonGrey}`,
  },
  buttonsRoot: {
    borderTop: `1px solid ${cartTheme.commonGrey}`,
  },
  dialogPaper: {
    minWidth: '30%',
  },
}));

const initialState = {
  loading: false,
  bodyShape: '',
  steering: '',
  model: '',
  faultLocation: '',
  faultType: '',
  efaError: false,
  connectionError: false,
};

const LANGUAGE_EN = 'en';
const LANGUAGE_DE = 'de';

const FIELDS = {
  LOADING: 'loading',
  BODY_SHAPE: 'bodyShape',
  STEERING: 'steering',
  MODEL: 'model',
  FAULT_LOCATION: 'faultLocation',
  FAULT_TYPE: 'faultType',
  EFA_ERROR: 'efaError',
  CONNECTION_ERROR: 'connectionError',
} as const;

const DialogEVAWidget: React.FC<DialogEVAWidgetInterface> = (props) => {
  const { close, setEvaFields = () => null } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const [state, setState] = useState(initialState);
  const [modelSeries, setModelSeries] = useState([]);
  const [typeVersions, setTypeVersions] = useState({});
  const [steerings, setSteerings] = useState({});
  const [showEfa, setShowEfa] = useState(false);
  const [efaInput, setEfaInput] = useState({});
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const setModelSteerings = (oneModel: string) => {
    const modelSteerings = efaInput[oneModel].reduce((accumulator, model) => {
      if (STEERING[model]) {
        return { ...accumulator, [model]: STEERING[model] };
      }

      return accumulator;
    }, {});
    setSteerings(modelSteerings);
  };

  const handleStateChange = (prop: string, newValue) => {
    const newState = {
      ...state,
      [prop]: newValue,
    };

    if (prop === FIELDS.BODY_SHAPE) {
      setModelSteerings(newValue);
      newState.steering = '';
    }

    if (prop === FIELDS.MODEL) {
      newState.bodyShape = '';
      newState.steering = '';
      affectedVehicleServices
        .getEfaInput(newValue)
        .then((receivedEfaInput) => {
          const arrayTypeVersions = Object.keys(receivedEfaInput).reduce((accumulator, bodyShape) => {
            if (TYPE_VERSIONS[bodyShape]) {
              return { ...accumulator, [bodyShape]: TYPE_VERSIONS[bodyShape] };
            }

            return accumulator;
          }, []);
          setTypeVersions(arrayTypeVersions || []);
          setEfaInput(receivedEfaInput || {});
        });
    }
    setState(newState);
  };

  const getWidgetText = (root: HTMLElement | null, selector: string) => root
    ?.querySelector(selector)
    ?.textContent
    ?.trim() || '';

  const normalizeFaultType = (value?: string) => {
    const normalized = (value || '').trim();

    return normalized.toLowerCase() === 'please select' ? '' : normalized;
  };

  const saveChanges = () => {
    const buildWidgetLabel = (root: HTMLElement | null, numberSelector: string, textSelector: string) => {
      const number = getWidgetText(root, numberSelector);
      const text = getWidgetText(root, textSelector);

      return [number, text].filter(Boolean).join('');
    };
    const currentState = stateRef.current;
    const widgetRoot = document.getElementById(efaContentParentID);

    const faultLocationFromWidget = buildWidgetLabel(widgetRoot, '.P93_fault-location-number', '.P93_fault-location-text');
    const faultTypeFromWidget = buildWidgetLabel(widgetRoot, '.P93_fault-type-number', '.P93_fault-type-text');

    const fallbackFields = {
      faultLocation: faultLocationFromWidget || undefined,
      faultType: faultTypeFromWidget || undefined,
    };

    const descriptonDisplayFields = {
      selectedModelSeries: currentState.model,
      faultLocation: currentState.faultLocation || fallbackFields.faultLocation,
      faultType: normalizeFaultType(currentState.faultType || fallbackFields.faultType),
    };

    setEvaFields({ values: descriptonDisplayFields });

    close();
  };

  const prepareConnectionEfa = useCallback(async (baumuster: Baumuster) => {
    const oppositeLanguage = i18next.language === LANGUAGE_EN ? LANGUAGE_DE : LANGUAGE_EN;

    try {
      await prepareServiceWorker();
      checkConnection(
        {
          baumuster,
          dialogLanguage: i18next.language,
          dataLanguage: i18next.language,
          systemLanguage: oppositeLanguage,
          translationLanguage: oppositeLanguage,
        },
        () => {
          setState((prevState) => ({
            ...prevState,
            loading: false,
            efaError: false,
            connectionError: false,
          }));
        },
        (part) => {
          const fieldsToUpdate = {
            faultLocation: part.faultLocation.label,
            faultType: normalizeFaultType(part.faultType.label),
          };

          setState((prevState) => ({
            ...prevState,
            faultLocation: fieldsToUpdate.faultLocation,
            faultType: fieldsToUpdate.faultType,
          }));
          setEvaFields({ values: fieldsToUpdate });
        },
        () => {
          setState((prevState) => ({
            ...prevState,
            loading: false,
            efaError: true,
            connectionError: false,
          }));
        },
        () => {
          setState((prevState) => ({
            ...prevState,
            loading: false,
            efaError: false,
            connectionError: true,
          }));
        },
      );
    } catch {
      setState((prevState) => ({
        ...prevState,
        loading: false,
        efaError: false,
        connectionError: true,
      }));
    }
  }, [setEvaFields]);

  const searchEFA = useCallback(() => {
    const baumuster: Baumuster = {
      steeringType: '',
      bodyShape: '',
      model: '',
      engineType: '__',
      transmissionType: '_',
    };

    const currentState = stateRef.current;

    setState((prevState) => ({
      ...prevState,
      loading: true,
      efaError: false,
      connectionError: false,
    }));
    setShowEfa(true);
    baumuster.steeringType = currentState.steering;
    baumuster.bodyShape = currentState.bodyShape;
    baumuster.model = currentState.model.slice(1);
    prepareConnectionEfa(baumuster);
  }, [prepareConnectionEfa]);

  const clearMeasureInput = () => {
    setState((prevState) => ({
      ...prevState,
      [FIELDS.FAULT_LOCATION]: initialState.faultLocation,
      [FIELDS.FAULT_TYPE]: initialState.faultType,
    }));
    setShowEfa(false);
  };

  const onClickNextButton = () => {
    searchEFA();
  };

  const onClickBackButton = () => {
    if (showEfa) {
      clearMeasureInput();
    } else {
      close();
    }
  };

  useEffect(() => {
    affectedVehicleServices
      .getModelSeriesEfa()
      .then((receivedModelSeries) => {
        const arrayreceivedModelSeries = receivedModelSeries.reduce((accumulator, serie) => ({
          ...accumulator, [serie]: { EN: serie, DE: serie },
        }), {});
        setModelSeries(arrayreceivedModelSeries);
      });
  }, []);

  return (
    <>
      <Dialog
        open
        id="newCR-dialog"
        classes={{ paper: classes.dialogPaper }}
        maxWidth="md"
        onClose={close}
        aria-labelledby="newCR-dialog-title"
        disableEscapeKeyDown={state.loading}
        data-testid="new-cr-dialog"
      >
        <DialogContent>
          {state.loading && <Spinner data-testid="new-cr-dialog-loader" isVisible isMask />}

          {!showEfa && (
            <>
              <h3>{t('dialog.sampledAffectedVehicle')}</h3>
              <div className={styles.SampleAffectedVehicleContent}>
                <Dropdown
                  id="model-select"
                  label={t('dialog.modelType')}
                  options={modelSeries}
                  value={state.model}
                  handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleStateChange(FIELDS.MODEL, ev.target.value)}
                  formControlClass={styles.Size}
                />
              </div>
              <div className={styles.SampleAffectedVehicleContent}>
                <Dropdown
                  disabled={!state.model}
                  id="bodyshape-select"
                  label={t('dialog.bodyshape')}
                  options={typeVersions}
                  value={state.bodyShape}
                  handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleStateChange(FIELDS.BODY_SHAPE, ev.target.value)}
                  formControlClass={styles.Size}
                />
                <Dropdown
                  disabled={!state.model || !state.bodyShape}
                  id="steering-select"
                  label={t('dialog.steering')}
                  options={steerings}
                  value={state.steering}
                  handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleStateChange(FIELDS.STEERING, ev.target.value)}
                  formControlClass={styles.Size}
                />
              </div>
            </>
          )}

          {showEfa && (
            <>
              <h3>{t('dialog.affectedPart')}</h3>
              <div id={efaContentParentID} className={styles.RootEFA}>
                <section id={efaContentID} />
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions classes={{ root: classes.buttonsRoot }}>
          {!showEfa && (
            <Button
              id="next-btn"
              data-testid="next-btn"
              variant="contained"
              color="primary"
              disabled={!state.model || !state.bodyShape || !state.steering}
              onClick={onClickNextButton}
            >
              {t('dialog.next')}
            </Button>
          )}
          {showEfa && (
            <Button
              id="create-update-btn"
              data-testid="create-update-btn"
              disabled={state.loading}
              variant="contained"
              color="primary"
              onClick={saveChanges}
            >
              {t('dialog.save')}
            </Button>
          )}
          <Button
            id="back-cancel-btn"
            data-testid="back-cancel-btn"
            disabled={state.loading}
            color="primary"
            onClick={onClickBackButton}
          >
            {t(`dialog.${showEfa ? 'back' : 'cancel'}`)}
          </Button>
        </DialogActions>
      </Dialog>

      {(state?.efaError || state?.connectionError) && (
        <ConfirmDialog
          confirm={state?.efaError ? () => handleStateChange(FIELDS.EFA_ERROR, false) : () => searchEFA()}
          close={state?.efaError ? () => handleStateChange(FIELDS.EFA_ERROR, false) : () => handleStateChange(FIELDS.CONNECTION_ERROR, false)}
          title={t(`dialog.${state?.efaError ? 'efaNotFound' : 'efaConnectionError'}.title`)}
          message={t(`dialog.${state?.efaError ? 'efaNotFound' : 'efaConnectionError'}.message`)}
          confirmBtnText={t(`dialog.${state?.efaError ? 'ok' : 'tryAgain'}`)}
          closeBtnText={t('dialog.close')}
          noCloseButton={state?.efaError}
        />
      )}
    </>
  );
};

const actionCreators = {
  setHeaderLang: setHeaderLanguage,
};

export default connect(null, actionCreators)(DialogEVAWidget);
